import { MNSproCloudApi } from '..';
import {
  AvailableUserTemplate,
  CourseCombined,
  GetUpnRequest,
  GroupAssignment,
  GroupInformation,
  ModuleWithUserPermissions,
  User,
  UserCreateOptions,
  UserUpdateOptions,
  UserWithEmbeddedTemplate,
} from '../api/v2';
import {
  GraphApiEndpoint,
  createGraphApi,
} from '../../microsoft-graph/factory';

export class UserService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UsersApiFactory(config);

    return api;
  }
  static async getById(
    authToken: string,
    organizationId: string,
    userId: string,
  ): Promise<UserWithEmbeddedTemplate> {
    const api = UserService.getApi(authToken);
    const response = await api.get(organizationId, userId);

    return response.data;
  }

  /**
   * Gets the photo of a user as base64 encoded blob.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} userId
   * @return {*}  {Promise<string>}
   * @memberof UserService
   */
  static async getPhoto(
    authToken: string,
    organizationId: string,
    userId: string,
  ): Promise<string> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UsersApiFactory(config);
    const user = await api.getPhoto(organizationId, userId);

    return user.data;
  }

  /**
   * Returns a list of groups, the given user is member of
   */
  static async getGroupsOfUser(
    authToken: string,
    organizationId: string,
    userId: string,
  ): Promise<GroupAssignment[]> {
    const api = UserService.getApi(authToken);
    const response = await api.getGroupsOfUser(organizationId, userId);

    return response.data;
  }

  static async update(
    authToken: string,
    organizationId: string,
    userId: string,
    userUpdateOptions: UserUpdateOptions,
  ) {
    const api = UserService.getApi(authToken);

    const response = await api.update(
      organizationId,
      userId,
      userUpdateOptions,
    );

    return response.data;
  }

  static async create(
    authToken: string,
    organizationId: string,
    userCreateOptions: UserCreateOptions,
  ): Promise<User | null> {
    const api = UserService.getApi(authToken);

    const response = await api.create(organizationId, userCreateOptions);

    return response.data.user ?? null;
  }

  static async getUsersRange(
    authToken: string,
    organizationId: string,
    take?: number,
    nextLink?: string,
  ) {
    const api = UserService.getApi(authToken);
    const response = await api.getAll(organizationId, nextLink, take);

    return response.data;
  }

  static async getAvailableTemplates(
    authToken: string,
    organizationId: string,
  ): Promise<AvailableUserTemplate[]> {
    const api = UserService.getApi(authToken);
    const response = await api.getAvailableTemplates(organizationId);

    return response.data;
  }

  static async getUserGroups(
    authToken: string,
    organizationId: string,
  ): Promise<GroupInformation[]> {
    const api = UserService.getApi(authToken);
    const response = await api.getAllGroups(organizationId);

    return response.data;
  }

  static async getUserCourses(
    authToken: string,
    organisationId: string,
    userId: string,
    timePeriod: string,
  ): Promise<CourseCombined[]> {
    const api = UserService.getApi(authToken);
    const response = await api.getDetailsUserCourses(
      organisationId,
      userId,
      timePeriod,
    );

    return response.data;
  }

  static async getUserPermissions(
    authToken: string,
    organisationId: string,
    userId: string,
  ): Promise<ModuleWithUserPermissions[]> {
    const api = UserService.getApi(authToken);
    const response = await api.getDetailsUserPermissions(
      organisationId,
      userId,
    );

    return response.data;
  }

  static async deleteUser(
    authToken: string,
    organizationId: string,
    userId: string,
  ) {
    const api = UserService.getApi(authToken);
    await api._delete(organizationId, userId);
  }

  static async getDeletedUsers(authToken: string, organisationId: string) {
    const api = UserService.getApi(authToken);
    const response = await api.getAllDeleted(organisationId);

    return response.data;
  }

  static async restoreDeletedUser(
    authToken: string,
    organisationId: string,
    userId: string,
  ) {
    const api = UserService.getApi(authToken);
    await api.restoreUser(organisationId, userId);
  }

  static async getUpn(
    authToken: string,
    organisationId: string,
    getUpnRequest: GetUpnRequest,
  ): Promise<string> {
    const api = UserService.getApi(authToken);
    const response = await api.getUpn(organisationId, getUpnRequest);

    return response.data;
  }

  static async getAll(
    authToken: string,
    organisationId: string,
  ): Promise<UserWithEmbeddedTemplate[]> {
    const api = UserService.getApi(authToken);

    const allPaginatedUsers = await api.getAll(organisationId);
    let users: UserWithEmbeddedTemplate[] = allPaginatedUsers.data.users || [];
    let nextLink = allPaginatedUsers.data.nextLink;
    while (nextLink) {
      const response = await api.getAll(organisationId, nextLink);
      allPaginatedUsers.data = response.data;

      if (allPaginatedUsers.data.users) {
        users = [...users, ...allPaginatedUsers.data.users];
      }

      nextLink = allPaginatedUsers.data.nextLink;
    }
    return users;
  }

  static async getAllDomains(
    authToken: string,
    organisationId: string,
  ): Promise<string[]> {
    const api = UserService.getApi(authToken);
    const response = await api.getAllDomains(organisationId);

    return response.data;
  }

  static async updateUserAccountEnabled(
    authToken: string,
    organisationId: string,
    userId: string,
    accountEnabled: boolean,
  ): Promise<UserWithEmbeddedTemplate> {
    const api = UserService.getApi(authToken);
    const response = await api.updateUserAccountEnabled(
      organisationId,
      userId,
      {
        accountEnabled,
      },
    );

    return response.data as UserWithEmbeddedTemplate;
  }

  /**
   * Gets the photo of a user as a blob via graph.
   *
   * @static
   * @param {string} authToken
   * @param {string} organizationId
   * @param {string} userId
   * @return {*}  {Promise<string>}
   * @memberof UserService
   */
  static async getPhotoViaGraph(
    authToken: string,
    userId: string,
  ): Promise<Blob | null> {
    const api = createGraphApi(authToken, GraphApiEndpoint.V1, 'blob');
    const response = await api.get<Blob>(`/users/${userId}/photo/$value`, {
      validateStatus: status => status === 200 || status === 404,
    });
    return response.status === 200 ? response.data : null;
  }
}
